import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  Snackbar,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Breadcrumbs,
  Link,
  Tooltip,
  Collapse,
  InputAdornment,
  Divider,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Storage as StorageIcon,
  TableChart as TableChartIcon,
  Visibility as VisibilityIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Sort as SortIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';

interface Column {
  name: string;
  type: string;
  nullable?: boolean;
  primary?: boolean;
}

interface TableInfo {
  name: string;
  type: string;
  rowCount: number;
  description?: string;
}

interface TableData {
  connectionId: string;
  tableName: string;
  columns: Column[];
  rows: Record<string, any>[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

interface Connection {
  id: string;
  name: string;
  type: string;
  status: string;
}

// 🔧 新增：筛选相关接口
interface ColumnFilter {
  column: string;
  operator: 'contains' | 'equals' | 'startsWith' | 'endsWith' | 'greaterThan' | 'lessThan' | 'between' | 'isNull' | 'isNotNull';
  value: string | number | [string | number, string | number];
}

interface SortConfig {
  column: string;
  direction: 'asc' | 'desc';
}

interface FilterConfig {
  globalSearch: string;
  columnFilters: ColumnFilter[];
  sort: SortConfig | null;
  showNullValues: boolean;
}

export const TableManager: React.FC = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // URL参数
  const connectionId = searchParams.get('connection');
  const tableName = searchParams.get('table');
  
  // 状态
  const [connections, setConnections] = useState<Connection[]>([]);
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [tableData, setTableData] = useState<TableData | null>(null);
  const [selectedConnection, setSelectedConnection] = useState<Connection | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // 🔧 新增：筛选相关状态
  const [filterConfig, setFilterConfig] = useState<FilterConfig>({
    globalSearch: '',
    columnFilters: [],
    sort: null,
    showNullValues: true
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filteredRows, setFilteredRows] = useState<Record<string, any>[]>([]);
  
  // 编辑相关
  const [editDialog, setEditDialog] = useState(false);
  const [editingRow, setEditingRow] = useState<Record<string, any> | null>(null);
  const [editFormData, setEditFormData] = useState<Record<string, any>>({});
  
  // 新增相关
  const [createDialog, setCreateDialog] = useState(false);
  const [createFormData, setCreateFormData] = useState<Record<string, any>>({});
  
  // 分页
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  
  // 通知
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  const API_BASE_URL = 'https://d1-mysql-manager-api.5212025.xyz';

  // API调用
  const apiCall = async (endpoint: string, options: RequestInit = {}) => {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
  };

  // 加载连接列表
  const loadConnections = async () => {
    try {
      setLoading(true);
      const data = await apiCall('/api/connections');
      console.log('TableManager - 连接API响应:', data); // 调试日志
      
      // API返回格式是 { data: { connections: [...] } }
      const connections = data.data?.connections || data.connections || [];
      console.log('TableManager - 解析后的连接:', connections); // 调试日志
      setConnections(connections);
      
      // 如果URL中有connectionId，设置选中的连接
      if (connectionId) {
        const connection = connections.find((c: Connection) => c.id === connectionId);
        if (connection) {
          setSelectedConnection(connection);
          loadTables(connectionId);
        }
      }
    } catch (error) {
      console.error('加载连接失败:', error);
      setError('加载数据库连接失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载表列表
  const loadTables = async (connId: string) => {
    try {
      setLoading(true);
      const data = await apiCall(`/api/connections/${connId}/tables`);
      setTables(data.data?.tables || []);
      
      // 如果URL中有tableName，加载表数据
      if (tableName) {
        loadTableData(connId, tableName, page, limit);
      }
    } catch (error) {
      console.error('加载表列表失败:', error);
      setError('加载数据库表失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载表数据
  const loadTableData = async (connId: string, table: string, currentPage: number = 1, currentLimit: number = 20) => {
    try {
      setLoading(true);
      const data = await apiCall(`/api/connections/${connId}/tables/${table}/data?page=${currentPage}&limit=${currentLimit}`);
      setTableData(data.data);
    } catch (error) {
      console.error('加载表数据失败:', error);
      setError('加载表数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 🔧 新增：筛选和排序函数
  const applyFilters = (rows: Record<string, any>[]) => {
    let filtered = [...rows];

    // 全局搜索
    if (filterConfig.globalSearch.trim()) {
      const searchTerm = filterConfig.globalSearch.toLowerCase();
      filtered = filtered.filter(row =>
        Object.values(row).some(value =>
          value !== null && value !== undefined &&
          String(value).toLowerCase().includes(searchTerm)
        )
      );
    }

    // 列筛选
    filterConfig.columnFilters.forEach(filter => {
      filtered = filtered.filter(row => {
        const cellValue = row[filter.column];
        
        if (!filterConfig.showNullValues && (cellValue === null || cellValue === undefined)) {
          return false;
        }

        switch (filter.operator) {
          case 'contains':
            return cellValue && String(cellValue).toLowerCase().includes(String(filter.value).toLowerCase());
          case 'equals':
            return cellValue === filter.value;
          case 'startsWith':
            return cellValue && String(cellValue).toLowerCase().startsWith(String(filter.value).toLowerCase());
          case 'endsWith':
            return cellValue && String(cellValue).toLowerCase().endsWith(String(filter.value).toLowerCase());
          case 'greaterThan':
            return cellValue && Number(cellValue) > Number(filter.value);
          case 'lessThan':
            return cellValue && Number(cellValue) < Number(filter.value);
          case 'between':
            if (Array.isArray(filter.value) && filter.value.length === 2) {
              const numValue = Number(cellValue);
              return numValue >= Number(filter.value[0]) && numValue <= Number(filter.value[1]);
            }
            return false;
          case 'isNull':
            return cellValue === null || cellValue === undefined;
          case 'isNotNull':
            return cellValue !== null && cellValue !== undefined;
          default:
            return true;
        }
      });
    });

    // 排序
    if (filterConfig.sort) {
      filtered.sort((a, b) => {
        const aValue = a[filterConfig.sort!.column];
        const bValue = b[filterConfig.sort!.column];
        
        // 处理null值
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;
        
        let comparison = 0;
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          comparison = aValue.localeCompare(bValue);
        } else {
          comparison = Number(aValue) - Number(bValue);
        }
        
        return filterConfig.sort!.direction === 'desc' ? -comparison : comparison;
      });
    }

    return filtered;
  };

  // 处理筛选
  const handleGlobalSearch = (searchTerm: string) => {
    setFilterConfig(prev => ({ ...prev, globalSearch: searchTerm }));
  };

  // 添加列筛选
  const addColumnFilter = (column: string, operator: ColumnFilter['operator'], value: ColumnFilter['value']) => {
    setFilterConfig(prev => ({
      ...prev,
      columnFilters: [
        ...prev.columnFilters.filter(f => f.column !== column),
        { column, operator, value }
      ]
    }));
  };

  // 删除列筛选
  const removeColumnFilter = (column: string) => {
    setFilterConfig(prev => ({
      ...prev,
      columnFilters: prev.columnFilters.filter(f => f.column !== column)
    }));
  };

  // 设置排序
  const handleSort = (column: string) => {
    setFilterConfig(prev => {
      const currentSort = prev.sort;
      let newSort: SortConfig | null = null;
      
      if (!currentSort || currentSort.column !== column) {
        newSort = { column, direction: 'asc' };
      } else if (currentSort.direction === 'asc') {
        newSort = { column, direction: 'desc' };
      }
      // 如果是desc，则清除排序（newSort保持null）
      
      return { ...prev, sort: newSort };
    });
  };

  // 清除所有筛选
  const clearAllFilters = () => {
    setFilterConfig({
      globalSearch: '',
      columnFilters: [],
      sort: null,
      showNullValues: true
    });
  };

  // 获取列的数据类型（用于确定筛选选项）
  const getColumnDataType = (column: Column) => {
    const type = column.type.toLowerCase();
    if (type.includes('int') || type.includes('decimal') || type.includes('float') || type.includes('double')) {
      return 'number';
    } else if (type.includes('date') || type.includes('time')) {
      return 'date';
    }
    return 'text';
  };

  // 🔧 新增：检查是否为自动管理的字段
  const isAutoManagedField = (columnName: string, columnType: string) => {
    const name = columnName.toLowerCase();
    const type = columnType.toLowerCase();
    
    // 主键字段
    if (columnName === 'id' || name.includes('id') && (type.includes('auto_increment') || type.includes('serial'))) {
      return true;
    }
    
    // 时间戳字段
    if (name === 'createtime' || name === 'created_at' || name === 'create_time') {
      return true;
    }
    
    if (name === 'updatetime' || name === 'updated_at' || name === 'update_time' || name === 'modified_at') {
      return true;
    }
    
    return false;
  };

  // 🔧 新增：获取字段的默认值
  const getFieldDefaultValue = (column: Column) => {
    const name = column.name.toLowerCase();
    const type = column.type.toLowerCase();
    
    // 日期时间字段的默认值
    if (type.includes('date') || type.includes('time')) {
      if (name.includes('expiration') || name.includes('expire')) {
        // 过期时间默认为一年后
        const nextYear = new Date();
        nextYear.setFullYear(nextYear.getFullYear() + 1);
        return nextYear.toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm格式
      }
      
      // 其他日期时间字段默认为当前时间
      return new Date().toISOString().slice(0, 16);
    }
    
    // 布尔字段默认值
    if (type.includes('bool') || type.includes('tinyint(1)')) {
      return '0';
    }
    
    // 数字字段默认值
    if (type.includes('int') || type.includes('decimal') || type.includes('float')) {
      return '0';
    }
    
    return '';
  };

  // 🔧 新增：渲染智能表单字段
  const renderFormField = (column: Column, value: string, onChange: (value: string) => void, isCreate: boolean = false) => {
    const dataType = getColumnDataType(column);
    const isAutoManaged = isAutoManagedField(column.name, column.type);
    const isDisabled = column.primary || (isCreate && isAutoManaged);
    
    // 获取字段描述
    const getFieldDescription = () => {
      if (column.primary) return '主键不可编辑';
      if (isAutoManaged && isCreate) return '系统自动生成';
      return `类型: ${column.type}${column.nullable ? ' (可为空)' : ' (必填)'}`;
    };
    
    // 日期时间字段
    if (dataType === 'date' && !isDisabled) {
      return (
        <TextField
          key={column.name}
          fullWidth
          label={column.name}
          type="datetime-local"
          value={value || getFieldDefaultValue(column)}
          onChange={(e) => onChange(e.target.value)}
          disabled={isDisabled}
          helperText={getFieldDescription()}
          InputLabelProps={{
            shrink: true,
          }}
        />
      );
    }
    
    // 数字字段
    if (dataType === 'number' && !isDisabled) {
      return (
        <TextField
          key={column.name}
          fullWidth
          label={column.name}
          type="number"
          value={value || getFieldDefaultValue(column)}
          onChange={(e) => onChange(e.target.value)}
          disabled={isDisabled}
          helperText={getFieldDescription()}
        />
      );
    }
    
    // 布尔字段（使用下拉选择）
    if (column.type.toLowerCase().includes('bool') || column.type.toLowerCase().includes('tinyint(1)')) {
      return (
        <FormControl key={column.name} fullWidth disabled={isDisabled}>
          <InputLabel>{column.name}</InputLabel>
          <Select
            value={value || getFieldDefaultValue(column)}
            label={column.name}
            onChange={(e) => onChange(e.target.value)}
          >
            <MenuItem value="0">否 (0)</MenuItem>
            <MenuItem value="1">是 (1)</MenuItem>
          </Select>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
            {getFieldDescription()}
          </Typography>
        </FormControl>
      );
    }
    
    // 长文本字段
    if (column.type.toLowerCase().includes('text') || column.type.toLowerCase().includes('varchar') && column.type.includes('255')) {
      return (
        <TextField
          key={column.name}
          fullWidth
          label={column.name}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={isDisabled}
          helperText={getFieldDescription()}
          multiline={column.type.toLowerCase().includes('text')}
          rows={column.type.toLowerCase().includes('text') ? 3 : 1}
        />
      );
    }
    
    // 默认文本字段
    return (
      <TextField
        key={column.name}
        fullWidth
        label={column.name}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={isDisabled}
        helperText={getFieldDescription()}
      />
    );
  };

  // 选择连接
  const handleConnectionSelect = (connection: Connection) => {
    setSelectedConnection(connection);
    setTables([]);
    setTableData(null);
    setSearchParams({ connection: connection.id });
    loadTables(connection.id);
  };

  // 选择表
  const handleTableSelect = (table: TableInfo) => {
    setSearchParams({ connection: connectionId!, table: table.name });
    setPage(1);
    loadTableData(connectionId!, table.name, 1, limit);
  };

  // 返回连接列表
  const handleBackToConnections = () => {
    setSelectedConnection(null);
    setTables([]);
    setTableData(null);
    setSearchParams({});
  };

  // 返回表列表
  const handleBackToTables = () => {
    setTableData(null);
    setSearchParams({ connection: connectionId! });
  };

  // 分页处理
  const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
    if (connectionId && tableName) {
      loadTableData(connectionId, tableName, newPage, limit);
    }
  };

  // 编辑行
  const handleEditRow = (row: Record<string, any>) => {
    setEditingRow(row);
    setEditFormData({ ...row });
    setEditDialog(true);
  };

  // 新增数据
  const handleCreateRow = () => {
    // 🔧 修复：设置智能默认值
    const defaultData: Record<string, string> = {};
    
    if (tableData) {
      tableData.columns.forEach(column => {
        const isAutoManaged = isAutoManagedField(column.name, column.type);
        
        if (!column.primary && !isAutoManaged) {
          // 只为非主键和非自动管理字段设置默认值
          defaultData[column.name] = getFieldDefaultValue(column);
        }
      });
    }
    
    setCreateFormData(defaultData);
    setCreateDialog(true);
  };

  // 保存编辑数据
  const handleSaveEdit = async () => {
    if (!editingRow || !connectionId || !tableName || !tableData) return;

    try {
      // 🔧 修复：动态获取主键值
      const primaryKeyColumn = tableData.columns.find(col => col.primary);
      if (!primaryKeyColumn) {
        showSnackbar('表没有主键，无法编辑记录', 'error');
        return;
      }
      
      const primaryKeyValue = editingRow[primaryKeyColumn.name];
      if (!primaryKeyValue) {
        showSnackbar('无法获取主键值，无法编辑记录', 'error');
        return;
      }

      // 🔧 修复：处理数据格式化
      const processedData: Record<string, any> = {};
      
      Object.entries(editFormData).forEach(([key, value]) => {
        const column = tableData.columns.find(col => col.name === key);
        if (!column) return;
        
        const dataType = getColumnDataType(column);
        
        // 处理不同数据类型
        if (value === '' || value === null || value === undefined) {
          if (column.nullable) {
            processedData[key] = null;
          }
        } else if (dataType === 'date') {
          // 日期时间字段转换为MySQL格式
          try {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
              // 转换为MySQL DATETIME格式: YYYY-MM-DD HH:mm:ss
              processedData[key] = date.toISOString().slice(0, 19).replace('T', ' ');
            } else {
              processedData[key] = value;
            }
          } catch (e) {
            processedData[key] = value;
          }
        } else if (dataType === 'number') {
          processedData[key] = parseFloat(value) || 0;
        } else {
          processedData[key] = value;
        }
      });

      console.log('处理后的编辑数据:', processedData);

      await apiCall(`/api/connections/${connectionId}/tables/${tableName}/rows/${primaryKeyValue}`, {
        method: 'PUT',
        body: JSON.stringify(processedData),
      });

      showSnackbar('数据更新成功', 'success');
      setEditDialog(false);
      setEditingRow(null);
      
      // 重新加载表数据
      loadTableData(connectionId, tableName, page, limit);
    } catch (error) {
      console.error('更新数据失败:', error);
      showSnackbar((error as Error).message || '更新数据失败', 'error');
    }
  };

  // 保存新增数据
  const handleSaveCreate = async () => {
    try {
      if (!connectionId || !tableName) return;

      // 🔧 修复：处理数据格式化
      const processedData: Record<string, any> = {};
      
      Object.entries(createFormData).forEach(([key, value]) => {
        const column = tableData?.columns.find(col => col.name === key);
        if (!column) return;
        
        const dataType = getColumnDataType(column);
        const isAutoManaged = isAutoManagedField(column.name, column.type);
        
        // 跳过自动管理的字段
        if (isAutoManaged) return;
        
        // 处理不同数据类型
        if (value === '' || value === null || value === undefined) {
          if (column.nullable) {
            processedData[key] = null;
          } else {
            // 必填字段设置默认值
            processedData[key] = getFieldDefaultValue(column);
          }
        } else if (dataType === 'date') {
          // 日期时间字段转换为MySQL格式
          try {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
              // 转换为MySQL DATETIME格式: YYYY-MM-DD HH:mm:ss
              processedData[key] = date.toISOString().slice(0, 19).replace('T', ' ');
            } else {
              processedData[key] = value;
            }
          } catch (e) {
            processedData[key] = value;
          }
        } else if (dataType === 'number') {
          processedData[key] = parseFloat(value) || 0;
        } else {
          processedData[key] = value;
        }
      });

      console.log('处理后的新增数据:', processedData);

      await apiCall(`/api/connections/${connectionId}/tables/${tableName}/rows`, {
        method: 'POST',
        body: JSON.stringify(processedData)
      });

      showSnackbar('数据新增成功', 'success');
      setCreateDialog(false);
      setCreateFormData({});
      
      // 重新加载表数据
      loadTableData(connectionId, tableName, page, limit);
    } catch (error) {
      console.error('新增数据失败:', error);
      showSnackbar((error as Error).message || '新增数据失败', 'error');
    }
  };

  // 删除行
  const handleDeleteRow = async (row: Record<string, any>) => {
    if (!connectionId || !tableName || !tableData) return;

    if (!window.confirm('确定要删除这条记录吗？此操作不可撤销。')) return;

    try {
      // 🔧 修复：动态获取主键值
      const primaryKeyColumn = tableData.columns.find(col => col.primary);
      if (!primaryKeyColumn) {
        showSnackbar('表没有主键，无法删除记录', 'error');
        return;
      }
      
      const primaryKeyValue = row[primaryKeyColumn.name];
      if (!primaryKeyValue) {
        showSnackbar('无法获取主键值，无法删除记录', 'error');
        return;
      }

      await apiCall(`/api/connections/${connectionId}/tables/${tableName}/rows/${primaryKeyValue}`, {
        method: 'DELETE',
      });

      showSnackbar('数据删除成功', 'success');
      loadTableData(connectionId, tableName, page, limit);
    } catch (error) {
      console.error('删除数据失败:', error);
      showSnackbar((error as Error).message || '删除数据失败', 'error');
    }
  };

  // 显示通知
  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  // 获取列类型颜色
  const getColumnTypeColor = (type: string) => {
    if (type.includes('int') || type.includes('number')) return 'primary';
    if (type.includes('varchar') || type.includes('text') || type.includes('char')) return 'secondary';
    if (type.includes('date') || type.includes('time')) return 'warning';
    if (type.includes('decimal') || type.includes('float')) return 'info';
    return 'default';
  };

  // 权限检查
  const canEdit = user?.role?.name === 'Administrator' || user?.role?.name === 'User';
  const canDelete = user?.role?.name === 'Administrator';

  // 表级别权限检查
  const getTablePermissions = (tableName: string) => {
    console.log('TableManager - 检查表权限 (详细):', {
      tableName,
      user: user,
      userExists: !!user,
      role: user?.role,
      roleExists: !!user?.role,
      roleName: user?.role?.name,
      tablePermissions: user?.role?.tablePermissions,
      tablePermissionsType: typeof user?.role?.tablePermissions,
      tablePermissionsKeys: user?.role?.tablePermissions ? Object.keys(user?.role?.tablePermissions) : null,
      specificTablePerms: user?.role?.tablePermissions?.[tableName],
      isAdmin: user?.role?.name === 'Administrator'
    });
    
    // 🔧 修复：优先检查角色名称，确保管理员总是有完整权限
    if (user?.role?.name === 'Administrator') {
      console.log('TableManager - 管理员角色，直接授予完整权限');
      return ['read', 'create', 'update', 'delete'];
    }
    
    if (!user?.role?.tablePermissions) {
      console.log('TableManager - 没有表权限数据，使用角色名称检查');
      // 🔧 修复：如果没有tablePermissions，根据角色名称给予默认权限
      if (user?.role?.name === 'User') {
        console.log('TableManager - 普通用户默认权限: [read, create, update]');
        return ['read', 'create', 'update'];
      } else if (user?.role?.name === 'ReadOnly' || user?.role?.name === 'Read Only') {
        console.log('TableManager - 只读用户默认权限: [read]');
        return ['read'];
      }
      console.log('TableManager - 未知角色，无权限');
      return [];
    }
    
    // 检查通配符权限（所有表的权限）
    if (user.role.tablePermissions['*']) {
      console.log('TableManager - 通配符权限 (*)', user.role.tablePermissions['*']);
      return user.role.tablePermissions['*'];
    }
    
    // 检查特定表权限
    const specificPerms = user.role.tablePermissions[tableName] || [];
    console.log('TableManager - 特定表权限:', tableName, specificPerms);
    return specificPerms;
  };

  const canReadTable = (tableName: string) => {
    const perms = getTablePermissions(tableName);
    const canRead = perms.includes('read');
    console.log('TableManager - canReadTable:', tableName, canRead, perms);
    return canRead;
  };

  const canCreateInTable = (tableName: string) => {
    const perms = getTablePermissions(tableName);
    const canCreate = perms.includes('create');
    console.log('TableManager - canCreateInTable:', tableName, canCreate, perms);
    return canCreate;
  };

  const canUpdateInTable = (tableName: string) => {
    const perms = getTablePermissions(tableName);
    const canUpdate = perms.includes('update');
    console.log('TableManager - canUpdateInTable:', tableName, canUpdate, perms);
    return canUpdate;
  };

  const canDeleteFromTable = (tableName: string) => {
    const perms = getTablePermissions(tableName);
    const canDelete = perms.includes('delete');
    console.log('TableManager - canDeleteFromTable:', tableName, canDelete, perms);
    return canDelete;
  };

  useEffect(() => {
    loadConnections();
  }, []);

  useEffect(() => {
    if (connectionId && tableName) {
      loadTableData(connectionId, tableName, page, limit);
    }
  }, [connectionId, tableName, page, limit]);

  // 🔧 新增：应用筛选的useEffect
  useEffect(() => {
    if (tableData?.rows) {
      const filtered = applyFilters(tableData.rows);
      setFilteredRows(filtered);
    }
  }, [tableData?.rows, filterConfig]);

  // 渲染连接列表
  if (!selectedConnection) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StorageIcon />
          数据库表管理
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          选择一个数据库连接来查看和管理表数据
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 3 }}>
          {connections.map((connection) => (
            <Card 
              key={connection.id}
              sx={{ 
                cursor: 'pointer',
                '&:hover': { boxShadow: 3 },
                transition: 'box-shadow 0.2s'
              }}
              onClick={() => handleConnectionSelect(connection)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <StorageIcon color="primary" />
                  <Typography variant="h6">
                    {connection.name}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  类型: {connection.type.toUpperCase()}
                </Typography>
                <Chip 
                  label={connection.status === 'connected' ? '已连接' : '未连接'}
                  color={connection.status === 'connected' ? 'success' : 'default'}
                  size="small"
                />
              </CardContent>
            </Card>
          ))}
        </Box>

        {connections.length === 0 && !loading && (
          <Card sx={{ mt: 3 }}>
            <CardContent sx={{ textAlign: 'center', py: 5 }}>
              <StorageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                暂无数据库连接
              </Typography>
              <Typography variant="body2" color="text.secondary">
                请先在连接管理中添加数据库连接
              </Typography>
              <Button 
                variant="contained" 
                onClick={() => navigate('/connections')}
                sx={{ mt: 2 }}
              >
                管理连接
              </Button>
            </CardContent>
          </Card>
        )}
      </Box>
    );
  }

  // 渲染表列表
  if (!tableData) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Breadcrumbs>
            <Link 
              color="inherit" 
              href="#" 
              onClick={handleBackToConnections}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <StorageIcon fontSize="small" />
              数据库连接
            </Link>
            <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TableChartIcon fontSize="small" />
              {selectedConnection.name}
            </Typography>
          </Breadcrumbs>
        </Box>

        <Typography variant="h4" gutterBottom>
          表列表
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {selectedConnection.name} 中的数据表
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>表名</TableCell>
                <TableCell>类型</TableCell>
                <TableCell align="right">记录数</TableCell>
                <TableCell>描述</TableCell>
                <TableCell align="center">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tables.map((table) => (
                <TableRow key={table.name} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TableChartIcon color="primary" fontSize="small" />
                      <Typography variant="body2" fontWeight="medium">
                        {table.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={table.type} size="small" />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {table.rowCount.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {table.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="查看数据">
                      <IconButton 
                        size="small" 
                        onClick={() => handleTableSelect(table)}
                        color="primary"
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {tables.length === 0 && !loading && (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <TableChartIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              暂无数据表
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  // 渲染表数据
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs>
          <Link 
            color="inherit" 
            href="#" 
            onClick={handleBackToConnections}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <StorageIcon fontSize="small" />
            数据库连接
          </Link>
          <Link 
            color="inherit" 
            href="#" 
            onClick={handleBackToTables}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <TableChartIcon fontSize="small" />
            {selectedConnection.name}
          </Link>
          <Typography color="text.primary">
            {tableData.tableName}
          </Typography>
        </Breadcrumbs>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            {tableData.tableName}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            共 {tableData.pagination.total.toLocaleString()} 条记录
            {filteredRows.length !== tableData.rows.length && (
              <span> | 筛选后 {filteredRows.length} 条记录</span>
            )}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {(() => {
            const currentTableName = tableData.tableName;
            const canCreate = canCreateInTable(currentTableName);
            console.log('TableManager - 按钮显示检查:', {
              currentTableName,
              canCreate,
              userRole: user?.role?.name,
              allPerms: getTablePermissions(currentTableName)
            });
            
            return canCreate && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreateRow}
                sx={{ mr: 1 }}
              >
                新增数据
              </Button>
            );
          })()}
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <InputLabel>每页</InputLabel>
            <Select
              value={limit}
              label="每页"
              onChange={(e) => {
                const newLimit = Number(e.target.value);
                setLimit(newLimit);
                if (connectionId && tableName) {
                  loadTableData(connectionId, tableName, 1, newLimit);
                  setPage(1);
                }
              }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* 🔧 新增：筛选界面 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterListIcon />
              数据筛选
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                startIcon={showAdvancedFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              >
                高级筛选
              </Button>
              <Button
                size="small"
                startIcon={<ClearIcon />}
                onClick={clearAllFilters}
                disabled={!filterConfig.globalSearch && filterConfig.columnFilters.length === 0 && !filterConfig.sort}
              >
                清除筛选
              </Button>
            </Box>
          </Box>

          {/* 全局搜索 */}
          <TextField
            fullWidth
            size="small"
            placeholder="搜索所有列的内容..."
            value={filterConfig.globalSearch}
            onChange={(e) => handleGlobalSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: filterConfig.globalSearch && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => handleGlobalSearch('')}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          {/* 当前活动的筛选器 */}
          {(filterConfig.columnFilters.length > 0 || filterConfig.sort) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                活动筛选:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {filterConfig.sort && (
                  <Chip
                    icon={<SortIcon />}
                    label={`${filterConfig.sort.column} ${filterConfig.sort.direction === 'asc' ? '↑' : '↓'}`}
                    onDelete={() => setFilterConfig(prev => ({ ...prev, sort: null }))}
                    color="primary"
                    variant="outlined"
                  />
                )}
                {filterConfig.columnFilters.map((filter, index) => (
                  <Chip
                    key={index}
                    label={`${filter.column} ${filter.operator} ${Array.isArray(filter.value) ? filter.value.join('-') : filter.value}`}
                    onDelete={() => removeColumnFilter(filter.column)}
                    color="secondary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* 高级筛选 */}
          <Collapse in={showAdvancedFilters}>
            <Divider sx={{ my: 2 }} />
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={filterConfig.showNullValues}
                    onChange={(e) => setFilterConfig(prev => ({ ...prev, showNullValues: e.target.checked }))}
                  />
                }
                label="显示空值(NULL)记录"
                sx={{ mb: 2 }}
              />
              
              {/* 列筛选器 */}
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
                gap: 2 
              }}>
                {tableData.columns.map((column) => {
                  const existingFilter = filterConfig.columnFilters.find(f => f.column === column.name);
                  const dataType = getColumnDataType(column);
                  
                  return (
                    <Card variant="outlined" sx={{ p: 2 }} key={column.name}>
                      <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {column.name}
                        <Chip size="small" label={column.type} />
                        {column.primary && <Chip size="small" label="PK" color="primary" />}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>筛选条件</InputLabel>
                          <Select
                            value={existingFilter?.operator || ''}
                            label="筛选条件"
                            onChange={(e) => {
                              if (e.target.value) {
                                // 设置默认值
                                let defaultValue: ColumnFilter['value'] = '';
                                if (e.target.value === 'between') {
                                  defaultValue = ['', ''];
                                }
                                addColumnFilter(column.name, e.target.value as ColumnFilter['operator'], defaultValue);
                              } else {
                                removeColumnFilter(column.name);
                              }
                            }}
                          >
                            <MenuItem value="">无筛选</MenuItem>
                            <MenuItem value="contains">包含</MenuItem>
                            <MenuItem value="equals">等于</MenuItem>
                            <MenuItem value="startsWith">开头是</MenuItem>
                            <MenuItem value="endsWith">结尾是</MenuItem>
                            {dataType === 'number' && (
                              <>
                                <MenuItem value="greaterThan">大于</MenuItem>
                                <MenuItem value="lessThan">小于</MenuItem>
                                <MenuItem value="between">范围</MenuItem>
                              </>
                            )}
                            <MenuItem value="isNull">为空</MenuItem>
                            <MenuItem value="isNotNull">不为空</MenuItem>
                          </Select>
                        </FormControl>
                        
                        {existingFilter && existingFilter.operator !== 'isNull' && existingFilter.operator !== 'isNotNull' && (
                          <>
                            {existingFilter.operator === 'between' ? (
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="最小值"
                                  type={dataType === 'number' ? 'number' : 'text'}
                                  value={Array.isArray(existingFilter.value) ? existingFilter.value[0] : ''}
                                  onChange={(e) => {
                                    const currentValue = Array.isArray(existingFilter.value) ? existingFilter.value : ['', ''];
                                    addColumnFilter(column.name, existingFilter.operator, [e.target.value, currentValue[1]]);
                                  }}
                                />
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="最大值"
                                  type={dataType === 'number' ? 'number' : 'text'}
                                  value={Array.isArray(existingFilter.value) ? existingFilter.value[1] : ''}
                                  onChange={(e) => {
                                    const currentValue = Array.isArray(existingFilter.value) ? existingFilter.value : ['', ''];
                                    addColumnFilter(column.name, existingFilter.operator, [currentValue[0], e.target.value]);
                                  }}
                                />
                              </Box>
                            ) : (
                              <TextField
                                fullWidth
                                size="small"
                                label="筛选值"
                                type={dataType === 'number' ? 'number' : 'text'}
                                value={Array.isArray(existingFilter.value) ? '' : existingFilter.value}
                                onChange={(e) => addColumnFilter(column.name, existingFilter.operator, e.target.value)}
                              />
                            )}
                          </>
                        )}
                      </Box>
                    </Card>
                  );
                })}
              </Box>
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* 列信息 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            表结构
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {tableData.columns.map((column) => (
              <Chip
                key={column.name}
                label={`${column.name} (${column.type})`}
                color={getColumnTypeColor(column.type)}
                variant={column.primary ? 'filled' : 'outlined'}
                size="small"
              />
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* 数据表格 */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {tableData.columns.map((column) => (
                <TableCell 
                  key={column.name}
                  sx={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort(column.name)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {column.name}
                    {column.primary && (
                      <Chip label="PK" size="small" color="primary" sx={{ minWidth: 30, height: 20 }} />
                    )}
                    {/* 排序指示器 */}
                    {filterConfig.sort?.column === column.name && (
                      <SortIcon 
                        sx={{ 
                          fontSize: 16,
                          transform: filterConfig.sort.direction === 'desc' ? 'rotate(180deg)' : 'none',
                          transition: 'transform 0.2s'
                        }} 
                      />
                    )}
                  </Box>
                </TableCell>
              ))}
              {(() => {
                const currentTableName = tableData.tableName;
                const canUpdate = canUpdateInTable(currentTableName);
                const canDelete = canDeleteFromTable(currentTableName);
                const showActions = canUpdate || canDelete;
                
                return showActions && (
                  <TableCell align="center">操作</TableCell>
                );
              })()}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* 🔧 修改：使用筛选后的数据 */}
            {filteredRows.map((row, index) => (
              <TableRow key={index} hover>
                {tableData.columns.map((column) => (
                  <TableCell key={column.name}>
                    <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {row[column.name] === null ? (
                        <em style={{ color: '#666' }}>NULL</em>
                      ) : (
                        String(row[column.name])
                      )}
                    </Typography>
                  </TableCell>
                ))}
                {(() => {
                  const currentTableName = tableData.tableName;
                  const canUpdate = canUpdateInTable(currentTableName);
                  const canDelete = canDeleteFromTable(currentTableName);
                  const showActions = canUpdate || canDelete;
                  
                  return showActions && (
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {canUpdate && (
                          <Tooltip title="编辑">
                            <IconButton size="small" onClick={() => handleEditRow(row)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDelete && (
                          <Tooltip title="删除">
                            <IconButton size="small" color="error" onClick={() => handleDeleteRow(row)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  );
                })()}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 分页 */}
      {tableData.pagination.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={tableData.pagination.totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* 编辑对话框 */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>编辑记录</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2, mt: 1 }}>
            {tableData.columns.map((column) => (
              renderFormField(column, editFormData[column.name] || '', (value) => setEditFormData({
                ...editFormData,
                [column.name]: value
              }), false)
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)} startIcon={<CancelIcon />}>
            取消
          </Button>
          <Button onClick={handleSaveEdit} variant="contained" startIcon={<SaveIcon />}>
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* 新增对话框 */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>新增记录</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2, mt: 1 }}>
            {tableData.columns.map((column) => (
              renderFormField(column, createFormData[column.name] || '', (value) => setCreateFormData({
                ...createFormData,
                [column.name]: value
              }), true)
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)} startIcon={<CancelIcon />}>
            取消
          </Button>
          <Button onClick={handleSaveCreate} variant="contained" startIcon={<SaveIcon />}>
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* 通知 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}; 